import { create } from 'zustand';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabaseClient } from '../services/supabaseClient';

export interface Conversation {
  id: string;
  employer_user_id: string;
  user_id: string;
  job_posting_id: string | null;
  last_message_at: string;
  last_message_preview: string | null;
  last_message_sender_id: string | null;
  employer_unread_count: number;
  user_unread_count: number;
  created_at: string;
  partner_name?: string | null;
  partner_email?: string | null;
  job_title?: string | null;
}

export interface DirectMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  body: string;
  read_at: string | null;
  created_at: string;
}

interface MessageStore {
  conversations: Conversation[];
  currentConversation: Conversation | null;
  currentMessages: DirectMessage[];
  unreadCount: number;
  loading: boolean;
  error: Error | null;

  // Realtime channels
  _conversationChannel: RealtimeChannel | null;
  _userInboxChannel: RealtimeChannel | null;

  fetchConversations: () => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  fetchMessages: (conversationId: string) => Promise<void>;
  sendMessage: (conversationId: string, body: string) => Promise<void>;
  markRead: (conversationId: string) => Promise<void>;
  startConversation: (params: { userId: string; jobPostingId?: string }) => Promise<Conversation>;
  clearCurrent: () => void;

  // Realtime subscriptions
  subscribeToConversation: (conversationId: string) => void;
  unsubscribeFromConversation: () => void;
  subscribeToUserInbox: (userId: string) => void;
  unsubscribeFromUserInbox: () => void;
}

export const useMessageStore = create<MessageStore>((set, get) => ({
  conversations: [],
  currentConversation: null,
  currentMessages: [],
  unreadCount: 0,
  loading: false,
  error: null,
  _conversationChannel: null,
  _userInboxChannel: null,

  fetchConversations: async () => {
    set({ loading: true, error: null });
    try {
      const res = await fetch('/api/v1/conversations', { credentials: 'include' });
      if (!res.ok) throw new Error('대화 목록을 불러오지 못했습니다.');
      const data = await res.json();
      set({ conversations: data.data || [], loading: false });
    } catch (e) {
      set({ error: e as Error, loading: false });
    }
  },

  fetchUnreadCount: async () => {
    try {
      const res = await fetch('/api/v1/conversations/unread-count', { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      set({ unreadCount: data.count || 0 });
    } catch (e) {
      console.error('fetchUnreadCount failed:', e);
    }
  },

  fetchMessages: async (conversationId) => {
    set({ loading: true, error: null });
    try {
      const res = await fetch(`/api/v1/conversations/${conversationId}/messages`, {
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || '메시지를 불러오지 못했습니다.');
      }
      const data = await res.json();
      set({
        currentConversation: data.data.conversation,
        currentMessages: data.data.messages,
        loading: false,
      });
    } catch (e) {
      set({ error: e as Error, loading: false });
    }
  },

  sendMessage: async (conversationId, body) => {
    const res = await fetch(`/api/v1/conversations/${conversationId}/messages`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || '메시지 전송에 실패했습니다.');
    }
    const data = await res.json();
    // 낙관적 업데이트 — 자기 메시지는 즉시 화면에 추가 (Realtime broadcast가 같은 메시지를 다시 보내도 dedup)
    set((state) => {
      const exists = state.currentMessages.some((m) => m.id === data.data.id);
      return exists ? state : { currentMessages: [...state.currentMessages, data.data] };
    });
  },

  markRead: async (conversationId) => {
    try {
      await fetch(`/api/v1/conversations/${conversationId}/read`, {
        method: 'PUT',
        credentials: 'include',
      });
      set((state) => ({
        conversations: state.conversations.map((c) =>
          c.id === conversationId
            ? { ...c, employer_unread_count: 0, user_unread_count: 0 }
            : c,
        ),
      }));
      get().fetchUnreadCount();
    } catch (e) {
      console.error('markRead failed:', e);
    }
  },

  startConversation: async ({ userId, jobPostingId }) => {
    const res = await fetch('/api/v1/conversations', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, jobPostingId }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || '대화를 시작할 수 없습니다.');
    }
    const data = await res.json();
    return data.data as Conversation;
  },

  clearCurrent: () => set({ currentConversation: null, currentMessages: [] }),

  // -----------------------------------------
  // Realtime broadcast 구독
  // -----------------------------------------
  subscribeToConversation: (conversationId) => {
    // 기존 채널 정리
    get().unsubscribeFromConversation();

    const channel = supabaseClient
      .channel(`dm:conv:${conversationId}`, { config: { broadcast: { self: true } } })
      .on('broadcast', { event: 'new_message' }, (payload: any) => {
        const incoming: DirectMessage | undefined = payload?.payload?.message;
        if (!incoming || incoming.conversation_id !== conversationId) return;
        set((state) => {
          const exists = state.currentMessages.some((m) => m.id === incoming.id);
          if (exists) return state;
          return { currentMessages: [...state.currentMessages, incoming] };
        });
        // 받은 즉시 markRead
        get().markRead(conversationId);
      })
      .on('broadcast', { event: 'messages_read' }, (payload: any) => {
        const readerId: string | undefined = payload?.payload?.readerId;
        const readAt: string | undefined = payload?.payload?.readAt;
        if (payload?.payload?.conversationId !== conversationId || !readerId || !readAt) return;
        // 상대(readerId)가 읽었으므로, 내가 보낸(=readerId가 아닌) 미읽 메시지를 '읽음'으로 갱신
        set((state) => ({
          currentMessages: state.currentMessages.map((m) =>
            m.sender_id !== readerId && !m.read_at ? { ...m, read_at: readAt } : m,
          ),
        }));
      })
      .subscribe();

    set({ _conversationChannel: channel });
  },

  unsubscribeFromConversation: () => {
    const ch = get()._conversationChannel;
    if (ch) {
      supabaseClient.removeChannel(ch);
      set({ _conversationChannel: null });
    }
  },

  subscribeToUserInbox: (userId) => {
    get().unsubscribeFromUserInbox();

    const channel = supabaseClient
      .channel(`dm:inbox:${userId}`)
      .on('broadcast', { event: 'inbox_update' }, () => {
        // 내 어떤 대화에든 새 메시지 도착 → 목록과 unread 카운트 갱신
        get().fetchConversations();
        get().fetchUnreadCount();
      })
      .subscribe();

    set({ _userInboxChannel: channel });
  },

  unsubscribeFromUserInbox: () => {
    const ch = get()._userInboxChannel;
    if (ch) {
      supabaseClient.removeChannel(ch);
      set({ _userInboxChannel: null });
    }
  },
}));
